import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';

import { useSiteScope } from '@/app/site/useSiteScope';
import type { RecordBody, TypeBody } from '@/shared/api/generated/model';
import { recordsList } from '@/shared/api/generated/records/records';
import { cx } from '@/shared/lib/cx';
import { describeWhen } from '@/shared/lib/dates';
import { toast } from '@/shared/ui/Toast';

import { useStageChange } from './useStageChange';
import { actionsFor, stageOf, statusesOf } from './stages';
import styles from './Stages.module.scss';

/** Сколько записей на доске: канбан — про текущие дела, не про архив. */
const LIMIT = 100;

/**
 * Канбан по статусу: колонка — статус, карточку переносят в другую колонку —
 * это переход (если он разрешён и доступен роли; с комментарием — через окно).
 */
export function Kanban({
  type,
  onOpen,
  subtitle,
}: {
  type: TypeBody;
  /** Открыть запись по щелчку. */
  onOpen: (record: RecordBody) => void;
  /** Вторая строка карточки; по умолчанию — когда заведена. */
  subtitle?: (record: RecordBody) => string;
}) {
  const scope = useSiteScope();
  const [over, setOver] = useState<string>();
  const statuses = statusesOf(type.workflow);

  const list = useQuery({
    queryKey: ['records', type.id, 'kanban'],
    queryFn: async () => {
      const response = await recordsList(type.id, {
        sort: 'created_at',
        order: 'desc',
        per_page: LIMIT,
      });

      if (response.status !== 200) {
        throw new Error('Неожиданный ответ на список записей');
      }

      return response.data;
    },
  });

  const { run, dialog } = useStageChange(() => {
    void list.refetch();
  });

  const records = list.data?.items ?? [];
  const writer = scope?.can('content.write') ?? false;

  const drop = (recordId: string, to: string) => {
    const record = records.find((item) => item.id === recordId);
    const from = record === undefined ? undefined : stageOf(type.workflow, record.stage);

    if (record === undefined || from === undefined || from.key === to) {
      return;
    }

    const transition = actionsFor(type.workflow, from.key, scope?.role).find((t) => t.to === to);
    const target = statusesOf(type.workflow).find((s) => s.key === to);

    if (transition === undefined) {
      toast.error(`Из «${from.name}» в «${target?.name ?? to}» перевести нельзя`, {
        description: 'Такого перехода нет в наборе статусов или он не для вашей роли.',
      });

      return;
    }

    run(record.id, transition);
  };

  return (
    <>
      {(list.data?.total ?? 0) > LIMIT && (
        <p className={styles['muted']}>
          На доске — последние {LIMIT}; остальные — в таблице с фильтром по статусу.
        </p>
      )}
      <div className={styles['board']}>
        {statuses.map((status) => {
          const cards = records.filter(
            (record) => stageOf(type.workflow, record.stage)?.key === status.key,
          );

          return (
            <section
              key={status.key}
              className={cx(
                styles['column'],
                styles[status.color ?? 'gray'],
                over === status.key && styles['column--target'],
              )}
              aria-label={status.name}
              onDragOver={(event) => {
                if (writer) {
                  event.preventDefault();
                  setOver(status.key);
                }
              }}
              onDragLeave={() => {
                setOver(undefined);
              }}
              onDrop={(event) => {
                event.preventDefault();
                setOver(undefined);
                drop(event.dataTransfer.getData('text/plain'), status.key);
              }}
            >
              <h3 className={styles['column__head']}>
                <span className={styles['dot']} aria-hidden="true" />
                {status.name}
                <span>{cards.length}</span>
              </h3>
              {cards.map((record) => (
                <button
                  key={record.id}
                  type="button"
                  className={styles['card']}
                  draggable={writer}
                  onDragStart={(event) => {
                    event.dataTransfer.setData('text/plain', record.id);
                  }}
                  onClick={() => {
                    onOpen(record);
                  }}
                >
                  <span className={styles['card__title']}>{record.title || 'Без названия'}</span>
                  <span className={styles['muted']}>
                    {subtitle?.(record) ?? describeWhen(record.created_at)}
                  </span>
                </button>
              ))}
            </section>
          );
        })}
      </div>
      {dialog}
    </>
  );
}
